import { apiClient } from './apiClient';
import {
  PaginatedResponse,
  AuditLog,
  CommissionReport,
  CommissionSummary,
  SurveyorMetrics,
  ServiceRequest,
  SurveyorProfile,
  User,
  Deal,
  Conversation,
  FlaggedMessage,
  CommissionDefault,
  PenaltyPayload,
  SuspendPayload,
  JobLead,
  JobLeadStatus,
  ApproveJobLeadPayload,
} from '../types';

export const adminService = {
  // ─── Requests ────────────────────────────────────────
  async listRequests(params?: {
    status?: string;
    service_type?: string;
    date_from?: string;
    date_to?: string;
    city?: string;
    page?: number;
    search?: string;
  }): Promise<PaginatedResponse<ServiceRequest>> {
    const { data } = await apiClient.get<PaginatedResponse<ServiceRequest>>(
      '/admin/requests/',
      { params },
    );
    return data;
  },

  async approveRequest(id: string): Promise<ServiceRequest> {
    const { data } = await apiClient.post<ServiceRequest>(`/admin/requests/${id}/approve/`);
    return data;
  },

  async rejectRequest(id: string, reason?: string): Promise<ServiceRequest> {
    const { data } = await apiClient.post<ServiceRequest>(`/admin/requests/${id}/reject/`, { reason });
    return data;
  },

  async unrejectRequest(id: string): Promise<ServiceRequest> {
    const { data } = await apiClient.post<ServiceRequest>(`/admin/requests/${id}/unreject/`);
    return data;
  },

  async assignRequest(id: string, surveyorId: string): Promise<ServiceRequest> {
    const { data } = await apiClient.post<ServiceRequest>(
      `/admin/requests/${id}/assign/`,
      { surveyor_id: surveyorId },
    );
    return data;
  },

  async unassignRequest(id: string, reason?: string): Promise<ServiceRequest> {
    const { data } = await apiClient.post<ServiceRequest>(
      `/admin/requests/${id}/unassign/`,
      { reason },
    );
    return data;
  },

  // ─── Users ───────────────────────────────────────────
  async listUsers(params?: {
    role?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<User>> {
    const { data } = await apiClient.get<PaginatedResponse<User>>(
      '/admin/users/',
      { params },
    );
    return data;
  },

  // ─── Deals ───────────────────────────────────────────
  async listDeals(params?: {
    status?: string;
    surveyor_id?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
  }): Promise<PaginatedResponse<Deal>> {
    const { data } = await apiClient.get<PaginatedResponse<Deal>>(
      '/admin/deals/',
      { params },
    );
    return data;
  },

  // ─── Surveyors ───────────────────────────────────────
  async getSurveyor(id: string): Promise<SurveyorProfile> {
    const { data } = await apiClient.get<SurveyorProfile>(`/admin/surveyors/${id}/`);
    return data;
  },

  async listInterviewQueue(params?: {
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<SurveyorProfile>> {
    const { data } = await apiClient.get<PaginatedResponse<SurveyorProfile>>(
      '/admin/surveyors/interview_queue/',
      { params },
    );
    return data;
  },

  async listSurveyors(params?: {
    service_type?: string;
    city?: string;
    verification_status?: string;
    interview_queue?: boolean;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<SurveyorProfile>> {
    const { data } = await apiClient.get<PaginatedResponse<SurveyorProfile>>(
      '/admin/surveyors/',
      { params },
    );
    return data;
  },

  async approveSurveyor(id: string, comments?: string): Promise<SurveyorProfile> {
    const { data } = await apiClient.post<SurveyorProfile>(
      `/admin/surveyors/${id}/approve/`,
      { comments },
    );
    return data;
  },

  async listClients(params?: {
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<User>> {
    const { data } = await apiClient.get<PaginatedResponse<User>>(
      '/admin/clients/',
      { params },
    );
    return data;
  },

  async rejectSurveyor(id: string, reason?: string): Promise<SurveyorProfile> {
    const { data } = await apiClient.post<SurveyorProfile>(
      `/admin/surveyors/${id}/reject/`,
      { reason },
    );
    return data;
  },

  // ─── Metrics ─────────────────────────────────────────
  async getSurveyorMetrics(surveyorId?: string): Promise<SurveyorMetrics[]> {
    const { data } = await apiClient.get<SurveyorMetrics[]>(
      '/admin/metrics/surveyors/',
      { params: surveyorId ? { surveyor_id: surveyorId } : undefined },
    );
    return data;
  },

  // ─── Commission Reports ──────────────────────────────
  async getCommissionReports(params?: {
    date_from?: string;
    date_to?: string;
    surveyor_id?: string;
    page?: number;
  }): Promise<PaginatedResponse<CommissionReport>> {
    const { data } = await apiClient.get<PaginatedResponse<CommissionReport>>(
      '/admin/reports/commissions/',
      { params },
    );
    return data;
  },

  async getCommissionSummary(params?: {
    period?: 'weekly' | 'monthly';
    date_from?: string;
    date_to?: string;
  }): Promise<CommissionSummary> {
    const { data } = await apiClient.get<CommissionSummary>(
      '/admin/reports/commissions/summary/',
      { params },
    );
    return data;
  },

  // ─── Audit Logs ──────────────────────────────────────
  async getAuditLogs(params?: {
    actor_id?: string;
    action?: string;
    entity_type?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<AuditLog>> {
    const { data } = await apiClient.get<PaginatedResponse<AuditLog>>(
      '/admin/audit-logs/',
      { params },
    );
    return data;
  },

  // ─── Settings ────────────────────────────────────────
  async getSettings(): Promise<Record<string, any>> {
    const { data } = await apiClient.get('/admin/settings/');
    return data;
  },

  async updateSettings(payload: Record<string, any>): Promise<Record<string, any>> {
    const { data } = await apiClient.patch('/admin/settings/', payload);
    return data;
  },

  // ─── Anti-Cheat Enforcement ─────────────────────────
  async suspendSurveyor(id: string, payload: SuspendPayload): Promise<SurveyorProfile> {
    const { data } = await apiClient.post<SurveyorProfile>(`/admin/surveyors/${id}/suspend/`, payload);
    return data;
  },

  async unsuspendSurveyor(id: string): Promise<SurveyorProfile> {
    const { data } = await apiClient.post<SurveyorProfile>(`/admin/surveyors/${id}/unsuspend/`);
    return data;
  },

  async addPenalty(id: string, payload: PenaltyPayload): Promise<SurveyorProfile> {
    const { data } = await apiClient.post<SurveyorProfile>(`/admin/surveyors/${id}/penalties/`, payload);
    return data;
  },

  async listFlaggedMessages(params?: { page?: number }): Promise<PaginatedResponse<FlaggedMessage>> {
    const { data } = await apiClient.get<PaginatedResponse<FlaggedMessage>>('/admin/chat/flagged/', { params });
    return data;
  },

  async dismissFlaggedMessage(id: string): Promise<void> {
    await apiClient.post(`/admin/chat/flagged/${id}/dismiss/`);
  },

  async listCommissionDefaults(params?: { status?: string; page?: number }): Promise<PaginatedResponse<CommissionDefault>> {
    const { data } = await apiClient.get<PaginatedResponse<CommissionDefault>>('/admin/commissions/defaults/', { params });
    return data;
  },

  async markCommissionPaid(dealId: string): Promise<void> {
    await apiClient.post(`/admin/commissions/${dealId}/mark-paid/`);
  },

  // ─── Conversations (Chat) ────────────────────────────
  async listConversations(params?: {
    type?: string;
    is_blocked?: boolean;
    page?: number;
  }): Promise<PaginatedResponse<Conversation>> {
    const { data } = await apiClient.get<PaginatedResponse<Conversation>>(
      '/admin/conversations/',
      { params },
    );
    return data;
  },

  async blockConversation(conversationId: string, reason: string): Promise<Conversation> {
    const { data } = await apiClient.post<Conversation>(
      `/admin/conversations/${conversationId}/block/`,
      { reason },
    );
    return data;
  },

  async unblockConversation(conversationId: string): Promise<Conversation> {
    const { data } = await apiClient.post<Conversation>(
      `/admin/conversations/${conversationId}/unblock/`,
    );
    return data;
  },

  // ─── Dashboard ───────────────────────────────────────
  async getDashboardStats(): Promise<{
    total_clients: number;
    total_surveyors: number;
    total_requests: number;
    active_negotiations: number;
    confirmed_deals: number;
    total_commission: number;
    pending_reviews: number;
  }> {
    const { data } = await apiClient.get('/admin/dashboard/');
    return data;
  },

  // ─── Job Leads (Surveyor Referrals) ─────────────────
  async listJobLeads(params?: {
    status?: JobLeadStatus;
    page?: number;
    search?: string;
  }): Promise<PaginatedResponse<JobLead>> {
    const { data } = await apiClient.get<PaginatedResponse<JobLead>>(
      '/admin/job-leads/',
      { params },
    );
    return data;
  },

  async approveJobLead(id: string, payload: ApproveJobLeadPayload): Promise<JobLead> {
    const { data } = await apiClient.post<JobLead>(
      `/admin/job-leads/${id}/approve/`,
      payload,
    );
    return data;
  },

  async rejectJobLead(id: string, reason: string): Promise<JobLead> {
    const { data } = await apiClient.post<JobLead>(
      `/admin/job-leads/${id}/reject/`,
      { reason },
    );
    return data;
  },

  async unrejectJobLead(id: string): Promise<JobLead> {
    const { data } = await apiClient.post<JobLead>(`/admin/job-leads/${id}/unreject/`);
    return data;
  },
};
